import React, { useState, useEffect } from 'react'
import Maybe from 'components/__new/common/Maybe';
import { ReactComponent as IconTimes } from "assets/images/icons/project-icons/times.svg"
import { ReactComponent as IconBack } from "assets/images/icons/project-icons/arrow_back.svg"
import { useSelector, useDispatch } from 'react-redux';
import * as banksActions from "store/ui/banks/action";
import * as walletAction from "store/entities/wallet/action";
import useLoginInfo from 'hooks/useLoginInfo';
import FormDrawer from 'components/__new/common/form-drawer';
import { clearRecurringTransferForm, getTransferBeneficiaries, saveRecurringTransferForm, updateRecurringTransfer } from 'store/entities/transfers/action';
import EditRecurringTransferAccountDetails from './EditRecurringTransferAccountDetails';
import EditRecurringTransferFrequency from './EditRecurringTransferFrequency';
import { toast } from 'react-toastify';
import AccessPinModals from '../AccessPinModals';
import useAccessPinStatus from 'hooks/useAccessPinStatus';
import useBanks from 'hooks/useBanks';


const RecurringTransferEdit = ({isOpen, toggleEditModal, getRecurringTransfers}) => {
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const [currentStep, setCurrentStep] = useState(1);
        
    const [validationError, setValidationError] = useState(null);
    const {getAccessPinStatus} = useAccessPinStatus();
    
    const [beneficiariesShown, setBeneficiariesShown] = useState(false);
    const [validatedBeneficiary, setValidatedBeneficiary] = useState(null);
        
    const [isOpenOtpNewPin, setIsOpenOtpNewPin] = useState(false);
    const [isOpenAccessPin, setIsOpenAccessPin] = useState(false);
    const [isOpenNewAccessPin, setIsOpenNewAccessPin] = useState(false);
    
    const [transferPayload, setTransferPayload] = useState(null);
    const [isOpenConfirmAccessPin, setIsOpenConfirmAccessPin] = useState(false);
    const [isOpenModalAlertTransferSuccess, setIsOpenModalAlertTransferSuccess] = useState(false);
    const [isOpenModalAlertNewPinSuccess, setIsOpenModalAlertNewPinSuccess] = useState(false);
    
        const [pin, setPin] = useState({
        otp: "",
        accessPin: "",
        accessPinConfirm: "",
    })
    
    const { banks, banksLoading } = useBanks(userInfo?.token);
    const {
        dashboard,
        accountValidationLoading: isValidating,
        accountValidation,
        accountValidationError
    } = useSelector((s) => s.entities.wallet);
    const {
        recurringTransferForm,
        updateRecurringLoading,
        createRecurringData,
        selectedRecurringTransfer: selected,
        updateRecurringData
    } = useSelector((s) => s.entities.transfers);
    
    const isCreatingDetails = currentStep === 1;
    
    const defaultForm = {
        name: "",
        amount: "",
        recipient: {
            accountNumber: "",
            accountName: "",
            bankName: "",
            bankCode: "",
        },
        frequency: "",
        time: "12:00",
        dayOfWeek: "",
        dayOfMonth: "",
        startDate: "",
        endDate: "",
    }

    const [form, setForm] = useState(defaultForm);
    
    const isDaily = form.frequency === "daily"
    const isWeekly = form.frequency === "weekly"
    const isMonthly = form.frequency === "monthly"
    
    const selectBeneficiary = (beneficiary) => {
        const { accountName, accountNumber, bankName, bankCode } = beneficiary
        
        setForm({
            ...form,
            recipient: {
                accountNumber,
                accountName,
                bankName,
                bankCode,
            }
        });
        setValidatedBeneficiary(accountName);
        setBeneficiariesShown(false);
    }
    
    const resetPins = () => {
        setPin({ ...pin, otp: "", accessPin: "", accessPinConfirm: "" });
    }
    
    const onProceed = () => {
        if(isCreatingDetails) {
            
            if(form?.name?.length < 1 || validatedBeneficiary === null || form?.amount?.length < 1) {
                toast.error("Please fill in all fields")
            } else {
                dispatch(saveRecurringTransferForm(form));
                setCurrentStep(2);
            }
        } else if(currentStep === 2) {
            if(form?.frequency?.length < 1) {
                toast.error("Please select a frequency")
            } else if((isDaily || isWeekly) && !form.time?.length) {
                toast.error("Please select a time")
            } else if(isWeekly && !form.dayOfWeek?.length) {
                toast.error("Please select a day of week")
            } else if(isMonthly && !form.dayOfMonth?.length) {
                toast.error("Please select a day of month")
            } else if(!form.startDate?.length || !form?.endDate?.length) {
                toast.error("Please select start and end dates")
            } else {
                const hours = parseInt(form?.time)?.toString()?.split(':')[0]
                const period = hours >= 12 ? 'PM' : 'AM';
                
                const payload = {
                    ...form,
                    time: `${form?.time}${period}`,
                    dayOfWeek: isWeekly ? form.dayOfWeek : "",
                    dayOfMonth: isMonthly ? form.dayOfMonth : ""
                }
                
                for (let key in payload) {
                    if (payload.hasOwnProperty(key) && payload[key] === "") {
                        delete payload[key];
                    }
                }
                
                setTransferPayload(payload);
                
                const hasAccessPin = getAccessPinStatus(dashboard);
                if (hasAccessPin === null || hasAccessPin === false){
                    setIsOpenNewAccessPin(true);
                    toggleEditModal();
                }
                else if (hasAccessPin === true){
                    toggleEditModal();
                    setIsOpenAccessPin(true);
                }
            }
        }
    }
    
    const onSubmit = () => {
        const bankObj = banks?.find((bank) => bank?.code === form?.recipient?.bankCode);
        const bankName = bankObj?.name;
        
        const payload = {
            ...transferPayload,
            bankName,
            accessPin: pin.accessPin,
            amount: Number(transferPayload?.amount),
        }
        dispatch(updateRecurringTransfer({
            id: selected?._id,
            payload
        }))
    }

    const onCancel = () => {
        if(isCreatingDetails) {
            toggleEditModal();
            dispatch(clearRecurringTransferForm());
        } else {
            setCurrentStep(1);
        }
    }
    
    useEffect(() => {
        if(form?.recipient?.bankCode?.length > 0 && form?.recipient?.accountNumber?.length === 10) {
            const params = { bankCode: form?.recipient.bankCode, accountNumber: form?.recipient.accountNumber };
            
            dispatch(walletAction.validateAccount(userInfo.token, params));
        }
        // eslint-disable-next-line
    }, [form.recipient?.bankCode, form.recipient?.accountNumber])
    
    useEffect(() => {
        if (accountValidation?.status === "success" && accountValidation?.data){
            const accountName = accountValidation?.data.account_name;
            
            setForm({...form, recipient: {...form.recipient, accountName}});
            setValidationError(null);
            setValidatedBeneficiary(accountName);
        }
        // eslint-disable-next-line
    }, [accountValidation])
    
    useEffect(() => {
        if(accountValidationError !== null) {
            setValidationError("Error Validating Account Details")
        }
        // eslint-disable-next-line
    }, [accountValidationError])
    
    useEffect(() => {
        dispatch(banksActions.getAllBanks());
        dispatch(getTransferBeneficiaries({ type: "inter" }))
        setValidatedBeneficiary(null);
        setValidationError(null);
        setForm(defaultForm)
        // eslint-disable-next-line
    }, [])
    
    
    useEffect(() => {
        if(recurringTransferForm !== null) {
            const { name, amount, frequency, startDate, endDate } = recurringTransferForm;
            
            setForm({
                ...form,
                name,
                amount,
                frequency,
                startDate,
                endDate,
            })
        }
        // eslint-disable-next-line
    }, [recurringTransferForm])
    
    
    useEffect(() => {
        if (createRecurringData?.message?.includes('success')){
            getRecurringTransfers();
            setIsOpenAccessPin(false);
            setIsOpenModalAlertTransferSuccess(true);
            dispatch(clearRecurringTransferForm())
            setCurrentStep(1);
            setForm(defaultForm);
            setValidatedBeneficiary(null);
        }
        // eslint-disable-next-line
    }, [createRecurringData])
    
    
    useEffect(() => {
        if (updateRecurringData?.message?.includes('success')){
            getRecurringTransfers();
            setIsOpenAccessPin(false);
            // setIsOpenModalAlertTransferSuccess(true);
            dispatch(clearRecurringTransferForm());
            setCurrentStep(1);
            setForm(defaultForm);
            setValidatedBeneficiary(null);
        }
        // eslint-disable-next-line
    }, [updateRecurringData])
    
    
    const disableSubmit = currentStep === 1 && ((isValidating || banksLoading || updateRecurringLoading) ||
        !form.name?.length || !form.recipient?.bankCode?.length || !form.amount?.length);
    
    return (
        <div className="w-full">
            <FormDrawer display="center" size="md" isOpen={isOpen} setIsOpen={() => {}}>
                <div className='w-full p-7'>
                    <div className='pb-2'>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center pb-1">
                                <Maybe condition={!isCreatingDetails}>
                                    <div className="w-7 h-7 flex items-center justify-center mr-3 bg-[#f4f4f4] rounded-md">
                                        <IconBack className='cursor-pointer' onClick={() => setCurrentStep(1)} />
                                    </div>
                                </Maybe>
                                
                                <div className="text-xl font-bold">
                                    Edit Recurring Transfer
                                </div>
                            </div>
                            
                            <div onClick={toggleEditModal} className='w-7 h-7 bg-[#f4f4f4] cursor-pointer rounded-md flex items-center justify-center'>
                                <IconTimes width={30} />
                            </div>
                        </div>
                        
                        <div className="pt-1">
                            Make recurring payments to recipients
                        </div>
                        
                        <div className="w-full flex items-center justify-between mt-5">
                            <div className="w-[47%]">
                                <div className='w-full h-1.5 rounded-lg bg-ep-primary-600' />
                                <p className="mb-0 text-ep-gray-700 pt-1">Transfer Details</p>
                            </div>
                            
                            <div className="w-[47%]">
                                <div className={`w-full h-1.5 rounded-lg ${isCreatingDetails ? "bg-[#e5e7eb]" : "bg-ep-primary-600"}`} />
                                <p className={`mb-0 ${isCreatingDetails ? "text-ep-gray-500" : "text-ep-gray-700"} pt-1`}>Set Frequency</p>
                            </div>
                        </div>
                    </div>
                    
                    <Maybe condition={currentStep === 1}>
                        <EditRecurringTransferAccountDetails
                            beneficiariesShown={beneficiariesShown}
                            setBeneficiariesShown={setBeneficiariesShown}
                            validatedBeneficiary={validatedBeneficiary}
                            setValidatedBeneficiary={setValidatedBeneficiary}
                            validationError={validationError}
                            setValidationError={setValidationError}
                            form={form}
                            setForm={setForm}
                            selectBeneficiary={selectBeneficiary}
                            banksLoading={banksLoading}
                            banks={banks}
                            defaultForm={defaultForm}
                            selected={selected}
                        />
                    </Maybe>
                    
                    <Maybe condition={currentStep === 2}>
                        <EditRecurringTransferFrequency 
                            form={form}
                            setForm={setForm}
                            defaultForm={defaultForm}
                            isDaily={isDaily}
                            isWeekly={isWeekly}
                            isMonthly={isMonthly}
                        />
                    </Maybe>
                
                    <div className="w-full flex items-center justify-end mt-7 pt-5">
                        <button disabled={updateRecurringLoading} type="button" onClick={onCancel} className="btn btn-transparent-primary mr-3">
                            {isCreatingDetails ? "Cancel" : "Back"}
                        </button>
                        
                        <button disabled={disableSubmit} onClick={onProceed} type="button" className="btn btn-ep-primary">
                            {isValidating ? "Validating..." : updateRecurringLoading ? "Updating..." : "Continue"}
                        </button>
                    </div>
                </div>
            </FormDrawer>
            
            <AccessPinModals 
                pin={pin}
                setPin={setPin}
                onSubmit={onSubmit}
                resetPins={resetPins}
                isOpenPin={isOpenAccessPin}
                setIsOpenPin={setIsOpenAccessPin}
                loading={updateRecurringLoading}
                setIsOpenAccessPin={setIsOpenAccessPin}
                isOpenNewAccessPin={isOpenNewAccessPin}
                setIsOpenNewAccessPin={setIsOpenNewAccessPin}
                isOpenConfirmAccessPin={isOpenConfirmAccessPin}
                setIsOpenConfirmAccessPin={setIsOpenConfirmAccessPin}
                isOpenOtpNewPin={isOpenOtpNewPin}
                setIsOpenOtpNewPin={setIsOpenOtpNewPin}
                isOpenModalAlertTransferSuccess={isOpenModalAlertTransferSuccess}
                setIsOpenModalAlertTransferSuccess={setIsOpenModalAlertTransferSuccess}
                isOpenModalAlertNewPinSuccess={isOpenModalAlertNewPinSuccess}
                setIsOpenModalAlertNewPinSuccess={setIsOpenModalAlertNewPinSuccess}
            />
            
        </div>
    )
}


export default RecurringTransferEdit